import ReactGA4 from 'react-ga4';
import { RouteParamsEvent } from 'src/types/routing';
import { cartStep } from 'src/utils/featureFlags';
import {
  CartItem,
  CartItemOnAddParams,
  CartItemOnRemoveParams,
} from '../../_components';
import {
  useCartStore,
  useCheckoutFormValuesStore,
  useCheckoutStore,
} from '../../stores';
import { putSellEdit } from '../../api';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMeQuery } from 'src/hooks/queries/user';
import { useEventContext } from '../../provider';
import { useEventData } from '../../hooks';
import ReactPixel from 'react-facebook-pixel';
import { Box } from '@chakra-ui/react';

const useDebounce = <T extends unknown>(value: T, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const CartItems = () => {
  const { eventID } = useParams<RouteParamsEvent>();
  const { coupon } = useParams();
  const meQuery = useMeQuery();
  const { getEventCart, setEventCart } = useCartStore();
  const { getEventCheckout } = useCheckoutStore();
  const { setNextButtonLoading } = useEventContext();
  const { formValues, setFormValues } = useCheckoutFormValuesStore();
  const event = useEventData();
  const eventCart = getEventCart(eventID!);
  const sellId = getEventCheckout(eventID!).sellId;

  const debouncedEventCart = useDebounce(eventCart, 600);

  const cartAddHandler = async ({ cartItem }: CartItemOnAddParams) => {
    if (sellId && meQuery.data?.email) {
      setNextButtonLoading(true);
    }

    const nextCartItems = eventCart.map(item =>
      item.ticketTypeId === cartItem.ticketTypeId
        ? { ...item, quantity: item.quantity + cartStep(item.ticketTypeId) }
        : item,
    );
    setEventCart(eventID!, nextCartItems);

    eventCart.forEach(item => {
      if (item.ticketTypeId === cartItem.ticketTypeId) {
        const contents = {
          id: eventID,
          quantity: item.quantity + 1,
        };

        ReactPixel.track('AddToCart', {
          content_type: 'product',
          content_ids: [eventID],
          value: item.price,
          contents: contents,
          content_name: event?.name,
          currency: 'BRL',
        });

        const cartItemGA4 = {
          item_category: 'product',
          item_list_id: eventID,
          price: item.price,
          quantity: item.quantity + 1,
          item_name: `${item.name}`,
        };
        ReactGA4.event('add_to_cart', {
          currency: 'BRL',
          value: item.price,
          items: [cartItemGA4],
        });
      }
    });
  };

  const cartRemoveHandler = ({ event, cartItem }: CartItemOnRemoveParams) => {
    event.stopPropagation();

    if (sellId && meQuery.data?.email) {
      setNextButtonLoading(true);

      if (Object.keys(formValues.owners).length > 0) {
        setFormValues({ owners: {} });
      }
    }

    const nextCartItems = eventCart.map(item =>
      item.ticketTypeId === cartItem.ticketTypeId
        ? { ...item, quantity: item.quantity - cartStep(item.ticketTypeId) }
        : item,
    );

    setEventCart(eventID!, nextCartItems);
  };

  useEffect(() => {
    formValues.owners = {};
    (async () => {
      if (sellId && meQuery.data?.email) {
        const payload = debouncedEventCart.map(item => ({
          ticketTypeId: item.ticketTypeId,
          quantity: item.quantity,
        }));


        if(payload.find(item => item.quantity > 0)) {
          const stock = await putSellEdit({
            sellId,
            payload,
            params: { coupon },
          });
  
          let cartAdjusted = false;
          const eventCartNewMaybe = debouncedEventCart.map(item => {
            const ticketTypeInStock = stock.find(
              stockItem => stockItem.id === item.ticketTypeId,
            );
            const requestMoreThanInStock =
              (ticketTypeInStock?.requestedQuantity ?? 0) >
              (ticketTypeInStock?.acceptedQuantity ?? 0);
  
            cartAdjusted = requestMoreThanInStock || cartAdjusted;
  
            return {
              ...item,
              quantity: ticketTypeInStock?.acceptedQuantity ?? 0,
              availableQuantity: requestMoreThanInStock
                ? ticketTypeInStock?.acceptedQuantity ?? 0
                : item.availableQuantity,
            };
          });
  
          if (cartAdjusted) {
            setEventCart(eventID!, eventCartNewMaybe);
          }
        }
      }

      setNextButtonLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    coupon,
    debouncedEventCart,
    eventID,
    meQuery.data?.email,
    sellId,
    setEventCart,
    setNextButtonLoading,
  ]);

  return (
    <Box w="full" borderBottom="2px" borderColor="gray.50">
      {eventCart.map(cartItem => (
        <CartItem
          pr="0"
          key={cartItem.ticketTypeId}
          onAdd={cartAddHandler}
          onRemove={cartRemoveHandler}
          {...{ cartItem }}
        />
      ))}
    </Box>
  );
};
